import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/formatDate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import ExportCsvButton from "@/components/ExportCsvButton";
import { Search, SlidersHorizontal, Inbox, UserCheck, Award, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Converted", "Disqualified"];

const STATUS_STYLES = {
    New: { bg: "#DBEAFE", text: "#1D4ED8", dot: "#3B82F6", label: "New" },
    Contacted: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B", label: "Contacted" },
    Converted: { bg: "#D1FAE5", text: "#047857", dot: "#10B981", label: "Converted" },
    Disqualified: { bg: "#F3F4F6", text: "#4B5563", dot: "#9CA3AF", label: "Disqualified" },
};

function StatusPill({ value }) {
    const s = STATUS_STYLES[value] || STATUS_STYLES.New;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium"
            style={{ background: s.bg, color: s.text }}
            data-testid={`lead-status-${value}`}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
            {s.label}
        </span>
    );
}

export default function Leads() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, by_status: { New: 0, Contacted: 0, Converted: 0, Disqualified: 0 } });
    const [search, setSearch] = useState("");
    const [statusF, setStatusF] = useState("all");
    const [sourceF, setSourceF] = useState("all");
    const [active, setActive] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const load = async () => {
        try {
            const [{ data: leadsData }, { data: statsData }] = await Promise.all([
                api.get("/leads"),
                api.get("/leads/stats"),
            ]);
            setLeads(leadsData);
            setStats(statsData);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load leads");
        }
    };
    useEffect(() => { load(); }, []);

    const sourceOptions = useMemo(() => {
        const set = new Set();
        leads.forEach((l) => { if (l.source) set.add(l.source); });
        return Array.from(set).sort();
    }, [leads]);

    const filtered = useMemo(() => {
        return leads.filter((l) => {
            if (search) {
                const hay = `${l.agency_name} ${l.agent_name} ${l.email}`.toLowerCase();
                if (!hay.includes(search.toLowerCase())) return false;
            }
            if (statusF !== "all" && (l.status || "New") !== statusF) return false;
            if (sourceF !== "all" && (l.source || "") !== sourceF) return false;
            return true;
        });
    }, [leads, search, statusF, sourceF]);

    const openLead = (l) => { setActive(l); setSheetOpen(true); };

    const onPatched = (updated) => {
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
        setActive(updated);
        // Optimistically refresh stats
        load();
    };

    const onConverted = ({ broker_id }) => {
        toast.success("Converted to broker — find it in the Broker Tracker");
        load();
        setSheetOpen(false);
        // Slight delay so the toast registers before nav
        setTimeout(() => { window.location.href = `/app/brokers?broker=${broker_id}`; }, 600);
    };

    const onDeleted = (id) => {
        setLeads((prev) => prev.filter((l) => l.id !== id));
        setSheetOpen(false);
        setActive(null);
        load();
    };

    return (
        <div className="p-6 lg:p-8 space-y-5 animate-fade-in" data-testid="leads-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="ph-h1">Leads</h1>
                    <p className="text-sm text-ph-muted mt-1">
                        Inbound broker appointment requests from the
                        {" "}<a href="/get-appointed" target="_blank" rel="noopener noreferrer" className="text-ph-primary inline-flex items-center gap-1 hover:underline" data-testid="open-landing-link">
                            Get Appointed page <ExternalLink className="w-3 h-3" />
                        </a>.
                    </p>
                </div>
                <ExportCsvButton path="/leads/export" filenamePrefix="players-health-leads" label="Export all leads" testid="export-leads-btn" />
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label="Total leads" value={stats.total} icon={Inbox} accent="bg-ph-primarySoft text-ph-primary" testid="stat-total" />
                <StatTile label="New" value={stats.by_status.New} icon={Inbox} accent="bg-blue-100 text-blue-700" testid="stat-new" />
                <StatTile label="Contacted" value={stats.by_status.Contacted} icon={UserCheck} accent="bg-amber-100 text-amber-700" testid="stat-contacted" />
                <StatTile label="Converted" value={stats.by_status.Converted} icon={Award} accent="bg-emerald-100 text-emerald-700" testid="stat-converted" />
            </div>

            {/* Filters */}
            <div className="ph-card p-4">
                <div className="flex items-center gap-2 mb-3">
                    <SlidersHorizontal className="w-3.5 h-3.5 text-ph-muted" />
                    <span className="text-xs font-medium text-ph-text">Filters</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="relative md:col-span-2">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-ph-mutedSoft" />
                        <Input
                            placeholder="Search agency, agent, or email…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-10"
                            data-testid="leads-search-input"
                        />
                    </div>
                    <Select value={statusF} onValueChange={setStatusF}>
                        <SelectTrigger className="h-10" data-testid="leads-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={sourceF} onValueChange={setSourceF}>
                        <SelectTrigger className="h-10" data-testid="leads-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All sources</SelectItem>
                            {sourceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Table */}
            <div className="ph-card overflow-hidden">
                <div className="max-h-[calc(100vh-360px)] overflow-auto scrollbar-thin">
                    <table className="w-full text-sm" data-testid="leads-table">
                        <thead className="bg-ph-canvas text-[11px] font-medium text-ph-muted sticky top-0 z-10 border-b border-ph-border">
                            <tr>
                                <th className="text-left px-4 py-2.5">Agency</th>
                                <th className="text-left px-4 py-2.5">Agent</th>
                                <th className="text-left px-4 py-2.5">Email</th>
                                <th className="text-left px-4 py-2.5">Phone</th>
                                <th className="text-left px-4 py-2.5">Location</th>
                                <th className="text-left px-4 py-2.5">Premium band</th>
                                <th className="text-left px-4 py-2.5">Source</th>
                                <th className="text-left px-4 py-2.5">Submitted</th>
                                <th className="text-left px-4 py-2.5">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-12 text-ph-muted">
                                    {leads.length === 0
                                        ? "No leads yet. When a broker submits the Get Appointed form, they'll land here."
                                        : "No leads match these filters."}
                                </td></tr>
                            )}
                            {filtered.map((l) => (
                                <tr
                                    key={l.id}
                                    onClick={() => openLead(l)}
                                    className="border-t border-ph-borderSoft cursor-pointer hover:bg-ph-canvas transition-colors"
                                    data-testid={`lead-row-${l.id}`}
                                >
                                    <td className="px-4 py-3 font-medium text-ph-text">{l.agency_name}</td>
                                    <td className="px-4 py-3 text-ph-muted">{l.agent_name}{l.title ? ` · ${l.title}` : ""}</td>
                                    <td className="px-4 py-3 text-ph-muted">{l.email}</td>
                                    <td className="px-4 py-3 text-ph-muted">{l.phone}</td>
                                    <td className="px-4 py-3 text-ph-muted">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</td>
                                    <td className="px-4 py-3 text-ph-muted">{l.premium_band || "—"}</td>
                                    <td className="px-4 py-3 text-ph-muted">{l.source || "—"}</td>
                                    <td className="px-4 py-3 text-ph-muted">{fmtDate(l.submitted_at)}</td>
                                    <td className="px-4 py-3"><StatusPill value={l.status || "New"} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 text-xs text-ph-muted border-t border-ph-border bg-ph-canvas">
                    Showing <span className="font-medium text-ph-text">{filtered.length}</span> of {leads.length} leads
                </div>
            </div>

            <LeadDetailSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                lead={active}
                onUpdated={onPatched}
                onConverted={onConverted}
                onDeleted={onDeleted}
            />
        </div>
    );
}

function StatTile({ label, value, icon: Icon, accent, testid }) {
    return (
        <div className="ph-card p-4 flex items-center gap-3" data-testid={testid}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
                <Icon className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] font-medium uppercase tracking-wider text-ph-muted">{label}</div>
                <div className="text-xl font-semibold text-ph-text tabular-nums">{value}</div>
            </div>
        </div>
    );
}
